import React from 'react'
import { FormattedMessage } from 'react-intl'
import { PureQueryOptions } from 'apollo-client'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { ApplyApplicationLicenseDialog, NoLicensesModal } from '../app-license'
import { WithTooltip } from '../../design-system'
import { panelTitleCss } from '../../legacy-design-system/text'
import { RoleSwitch, Role } from '../roles'
import { AgentFeature, isFeatureEnabledForLanguage } from '../agent-features'
import { AgentLanguage } from '../agent-language'
import { Link } from '@contrast-security-inc/design-system-typography'

const appUnlicensedDisabledTextCss = css`
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  font-size: 12px;
`

function AppUnlicensedDisabledText() {
  return (
    <FormattedMessage id="title_applicationStatusUnlicensed">
      {(text) => (
        <span
          className={cx(panelTitleCss, appUnlicensedDisabledTextCss)}
          children={text}
          data-testid="disabled-unlicensed-text"
        />
      )}
    </FormattedMessage>
  )
}

function AppUnlicensedAssessNotSupportedText({
  applicationLanguage
}: {
  readonly applicationLanguage: AgentLanguage
}) {
  return (
    <WithTooltip
      children={() => {
        return (
          <FormattedMessage id="title_applicationStatusUnlicensed">
            {(text) => <span className={disabledLinkCss}>{text}</span>}
          </FormattedMessage>
        )
      }}
      renderTooltipContent={() => {
        return (
          <FormattedMessage
            id="variable_applications_assessNotSupportedForApplications"
            values={{
              appLanguage: applicationLanguage.getDisplay()
            }}
          >
            {(text) => <div className={tooltipCss}>{text}</div>}
          </FormattedMessage>
        )
      }}
      placement="bottom"
    />
  )
}

const linkCss = css`
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
  text-transform: uppercase;
  color: ${theme.color.turquoise70};
  font-size: 12px;
`

const disabledLinkCss = cx(
  linkCss,
  css`
    color: ${theme.color.neutral50};
  `
)

const tooltipCss = css`
  max-width: 170px;
  font-size: 12px;
`

export interface AppUnlicensedLinkProps {
  readonly organizationID: string
  readonly applicationID: string
  readonly applicationName: string
  readonly applicationLanguage: AgentLanguage
  readonly features?: AgentFeature[]
  readonly hasLicenses: boolean
  readonly refetchQueries?: PureQueryOptions[]
  readonly refresh?: () => Promise<any>
  readonly archived: boolean
  readonly role: Role
  readonly className?: string
}

interface AppUnlicensedLinkState {
  readonly open: boolean
}
export class AppUnlicensedLink extends React.Component<
  AppUnlicensedLinkProps,
  AppUnlicensedLinkState
> {
  public state = {
    open: false
  }

  public render() {
    const {
      organizationID,
      applicationID,
      applicationName,
      applicationLanguage,
      hasLicenses,
      refetchQueries,
      refresh,
      archived,
      role,
      className
    } = this.props
    const { open } = this.state

    return this.doesLanguageSupportAssess() ? (
      archived ? (
        <AppUnlicensedDisabledText />
      ) : (
        <RoleSwitch
          role={role}
          requiredRole="ADMIN"
          renderAllowed={() => (
            <React.Fragment>
              <FormattedMessage id="title_applicationStatusUnlicensed">
                {(title) => {
                  return (
                    <Link
                      className={cx(linkCss, className)}
                      onClick={this.toggleOpen}
                      data-testid="enabled-unlicensed-link"
                    >
                      {title}
                    </Link>
                  )
                }}
              </FormattedMessage>

              {hasLicenses ? (
                <ApplyApplicationLicenseDialog
                  isOpen={open}
                  organizationID={organizationID}
                  application={{
                    id: applicationID,
                    name: applicationName
                  }}
                  closeDialog={this.toggleOpen}
                  refetchQueries={refetchQueries}
                  refresh={refresh}
                />
              ) : (
                <NoLicensesModal isOpen={open} closeDialog={this.toggleOpen} />
              )}
            </React.Fragment>
          )}
          renderDenied={() => <AppUnlicensedDisabledText />}
        />
      )
    ) : (
      <AppUnlicensedAssessNotSupportedText
        applicationLanguage={applicationLanguage}
      />
    )
  }

  private toggleOpen = () => {
    this.setState((prevState) => ({
      open: !prevState.open
    }))
  }

  private doesLanguageSupportAssess = () => {
    const { applicationLanguage, features } = this.props
    return (
      features &&
      features.length > 0 &&
      isFeatureEnabledForLanguage({
        agentFeatures: features,
        featureName: 'assess',
        language: applicationLanguage
      })
    )
  }
}
